import ConversationTransport from '@/services/conversation/transport/ConversationTransport'
import type { ContextMessage } from '@/api/contract'
import type { Bot } from '@/types/domain'
import type { StreamHandle } from '@/services/conversation/transport/ConversationTransport'

export const MESSAGE_REWRITE_SYSTEM_PROMPT = [
  'You rewrite a user\'s draft message.',
  'Improve its clarity, flow, vividness, and natural phrasing while preserving its meaning, intent, emotional tone, point of view, tense, speaker perspective, names, facts, and formatting where practical.',
  'Do not answer the draft, continue the conversation, add commentary, explain your changes, or wrap the result in quotation marks or Markdown fences.',
  'Return only the revised message.',
].join(' ')

export function buildMessageRewritePrompt(draft: string): ContextMessage[] {
  return [
    { role: 'system', content: MESSAGE_REWRITE_SYSTEM_PROMPT },
    { role: 'user', content: draft },
  ]
}

interface MessageRewriteServiceOptions {
  transport?: ConversationTransport
}

export interface StartMessageRewriteOptions {
  draft: string
  bot: Bot
  conversationId?: number | string | null
  onChunk?: (content: string) => Promise<void> | void
  onComplete?: (result: { content: string; aborted: boolean }) => Promise<void> | void
}

/**
 * Starts an ephemeral rewrite stream. This service deliberately owns no store
 * and performs no persistence; the caller decides whether the result is ever
 * promoted into a real user message.
 */
export class MessageRewriteService {
  private readonly transport: ConversationTransport

  constructor({ transport = new ConversationTransport() }: MessageRewriteServiceOptions = {}) {
    this.transport = transport
  }

  async start({
    draft,
    bot,
    conversationId = null,
    onChunk,
    onComplete,
  }: StartMessageRewriteOptions): Promise<StreamHandle> {
    if (!draft.trim()) throw new Error('A draft is required to start a rewrite.')

    return this.transport.generateStream({
      messages: buildMessageRewritePrompt(draft),
      botId: bot.id,
      maxTokens: bot.max_tokens,
      conversationId,
    }, {
      onContentDelta: (_chunk, content) => onChunk?.(content),
      onComplete: ({ content, aborted }) => onComplete?.({ content, aborted }),
    })
  }
}

export default MessageRewriteService
